import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
  Divider,
} from '@mui/material';
import { LockOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuthStore } from '@/stores/authStore';
import { authService } from '@/services/auth.service';
import { fileService } from '@/services/file.service';
import VersionBadge from '@/components/VersionBadge';
import { loginSchema, type LoginFormData } from '@/types/auth.types';
import type { MfaRequiredResponse } from '@/types/user.types';
import { useFieldErrors } from '@/utils/formErrors';

// QA #68: a wrong or expired code is the code field's problem; everything
// else (locked out, server down) stays in the Alert above the form.
const MFA_CODE_FIELDS = { INVALID_OTP: 'code', OTP_EXPIRED: 'code' };

type LoginStage = 'credentials' | 'mfa';

function isMfaRequired(result: any): result is MfaRequiredResponse {
  return result && result.requires_mfa === true;
}

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuthStore();

  const [loginStage, setLoginStage] = useState<LoginStage>('credentials');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // QA #68: server-side field problems (a VALIDATION_ERROR on username /
  // password, a bad MFA code) go under the input; `error` is for the rest.
  const fieldErrors = useFieldErrors();

  // MFA state
  const [mfaToken, setMfaToken] = useState('');
  const [mfaEmail, setMfaEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);
  // Optional login methods offered by this deployment (GET /auth/config).
  const [samlEnabled, setSamlEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    authService
      .getAuthConfig()
      .then((cfg) => {
        if (!cancelled) setSamlEnabled(cfg.saml_enabled);
      })
      .catch(() => {
        /* no config → password login only */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // React Hook Form with Zod validation
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  // SAML callback detection on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const samlToken = params.get('saml_token');
    if (samlToken) {
      setLoading(true);
      // Store the token so the API interceptor can use it
      localStorage.setItem('token', samlToken);
      authService
        .getProfile()
        .then((user) => {
          login(samlToken, user);
          void fileService.flushUploadFailures(); // QA #102
          window.history.replaceState({}, '', '/login');
          navigate('/overview');
        })
        .catch(() => {
          localStorage.removeItem('token');
          setError('SAML 登入失敗');
          setLoading(false);
        });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Resend countdown timer
  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setTimeout(() => setResendCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  // Handle credentials submit
  const onSubmit = async (data: LoginFormData) => {
    setError('');
    fieldErrors.clear();
    setLoading(true);

    try {
      const result = await authService.login(data);

      if (isMfaRequired(result)) {
        setMfaToken(result.mfa_token);
        setMfaEmail(result.email_hint || '');
        setLoginStage('mfa');
        setResendCountdown(60);
      } else {
        // Normal login
        login(result.token, result.user, result.refresh_token);
        // QA #102: upload failures kept while the session was dead go out now.
        void fileService.flushUploadFailures();
        if (result.user.must_change_password) {
          navigate('/password');
        } else {
          navigate('/overview');
        }
      }
    } catch (err) {
      // Wrong credentials are not a field error: one Alert, no field is blamed.
      setError(fieldErrors.apply(err, {}, '登入失敗，請檢查帳號密碼'));
    } finally {
      setLoading(false);
    }
  };

  // Handle MFA verification
  const handleMfaVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    fieldErrors.clear();

    if (otpCode.length !== 6) {
      fieldErrors.setField('code', '請輸入信件中的 6 位數驗證碼');
      return;
    }

    setLoading(true);
    try {
      const result = await authService.verifyMfa(mfaToken, otpCode);
      login(result.token, result.user, result.refresh_token);
      void fileService.flushUploadFailures(); // QA #102
      if (result.user.must_change_password) {
        navigate('/password');
      } else {
        navigate('/overview');
      }
    } catch (err) {
      setError(fieldErrors.apply(err, MFA_CODE_FIELDS, '驗證碼驗證失敗，請稍後再試'));
    } finally {
      setLoading(false);
    }
  };

  // Handle resend MFA code
  const handleResend = async () => {
    if (resendCountdown > 0) return;
    setError('');
    try {
      await authService.resendMfa(mfaToken);
      setResendCountdown(60);
    } catch (err) {
      setError(fieldErrors.apply(err, {}, '重新發送驗證碼失敗，請稍後再試'));
    }
  };

  // Back to credentials stage
  const handleBackToLogin = () => {
    setLoginStage('credentials');
    setMfaToken('');
    setMfaEmail('');
    setOtpCode('');
    setResendCountdown(0);
    setError('');
    fieldErrors.clear();
  };

  // Handle Azure AD SAML login
  const handleSamlLogin = () => {
    window.location.href = '/api/v1/auth/saml/login';
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <LockOutlined sx={{ color: 'white' }} />
          </Box>

          <Typography component="h1" variant="h5" gutterBottom>
            {loginStage === 'credentials' ? '登入' : '驗證碼驗證'}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {import.meta.env.VITE_APP_NAME || '備份系統'}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Stage 1: Credentials */}
          {loginStage === 'credentials' && (
            <>
              <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ width: '100%' }}>
                <TextField
                  margin="normal"
                  fullWidth
                  id="username"
                  label="帳號"
                  autoComplete="username"
                  autoFocus
                  disabled={loading}
                  error={!!errors.username || !!fieldErrors.errors.username}
                  helperText={errors.username?.message ?? fieldErrors.errors.username}
                  onInput={() => fieldErrors.clearField('username')}
                  {...register('username')}
                />

                <TextField
                  margin="normal"
                  fullWidth
                  label="密碼"
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  disabled={loading}
                  error={!!errors.password || !!fieldErrors.errors.password}
                  helperText={errors.password?.message ?? fieldErrors.errors.password}
                  onInput={() => fieldErrors.clearField('password')}
                  {...register('password')}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : '登入'}
                </Button>
              </Box>

              {/* ADR-017 (decision Q6): SAML is Deferred until Azure AD onboarding;
                  the button shows only when the backend says SAML is enabled. */}
              {samlEnabled && (
                <>
                  <Divider sx={{ width: '100%', my: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      或
                    </Typography>
                  </Divider>

                  <Button
                    fullWidth
                    variant="outlined"
                    sx={{ mt: 1 }}
                    onClick={handleSamlLogin}
                    disabled={loading}
                  >
                    使用 Azure AD 登入
                  </Button>
                </>
              )}

              {import.meta.env.DEV && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    預設帳號: admin / admin123
                  </Typography>
                </Box>
              )}
            </>
          )}

          {/* Stage 2: MFA Code */}
          {loginStage === 'mfa' && (
            <>
              <Alert severity="info" sx={{ width: '100%', mb: 2 }}>
                驗證碼已發送到 {mfaEmail || '您的信箱'}
              </Alert>

              <Box component="form" onSubmit={handleMfaVerify} sx={{ width: '100%' }}>
                <TextField
                  margin="normal"
                  fullWidth
                  id="otp-code"
                  label="驗證碼"
                  autoComplete="one-time-code"
                  autoFocus
                  disabled={loading}
                  value={otpCode}
                  onChange={(e) => {
                    // Only allow digits, max 6
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setOtpCode(val);
                  }}
                  inputProps={{
                    maxLength: 6,
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    style: { letterSpacing: '0.5em', textAlign: 'center', fontSize: '1.5rem' },
                  }}
                  {...fieldErrors.bind('code', '請輸入 6 位數驗證碼')}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={loading || otpCode.length !== 6}
                >
                  {loading ? <CircularProgress size={24} /> : '驗證'}
                </Button>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={handleBackToLogin}
                    underline="hover"
                  >
                    返回登入
                  </Link>

                  <Button
                    size="small"
                    onClick={handleResend}
                    disabled={resendCountdown > 0}
                  >
                    {resendCountdown > 0
                      ? `重新發送驗證碼 (${resendCountdown}s)`
                      : '重新發送驗證碼'}
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </Paper>

        {/* Build version under the card — the first thing to quote in a bug report. */}
        <VersionBadge align="center" sx={{ mt: 2 }} />
      </Box>
    </Container>
  );
}
